/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, FlatList, ListRenderItem, ListRenderItemInfo, useWindowDimensions, ViewStyle } from "react-native"
import { Button, ListEmpty, Screen, Text } from "../../../../components"
// import { useStores } from "../../models"
import { color, nativeBaseTheme } from "../../../../theme"
import { useStores } from "../../../../models"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { SafeAreaView } from "react-native-safe-area-context"
import { InfoItem, ItemFooter, InfoWarningModal } from "../overview-feed-cost-list/overview-feed-cost-list-screen"
import { View } from "native-base"
import numeral from "numeral"
import { translate } from "../../../../i18n"
import { ShrimpPrice, Currency } from "../../../../database"
import { navigate } from "../../../../navigators"
import Entypo from 'react-native-vector-icons/Entypo';
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center',
  backgroundColor: color.palette.white
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row'
}

const SETTING_BUTTON: ViewStyle = {
  marginBottom: 4
}

const Header: FC<{ colorScheme?: "light"|"dark", currency?: string }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), currency } = props;
  const { appStore: { farmStore }, shrimpPriceStore} = useStores();
  const { activeBatchStore, lastFarmShrimpPrice } = farmStore || FarmStoreModel.create({})
  const { predictionRevenue, cycles, isFeedActualWithin4Days, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})
  const [ farmShrimpPrice, setModelFarmShrimpPrice ] = useState<any>(null)

  useEffect(() => {
    if (!lastFarmShrimpPrice?.shrimp_price_id) return;
    const shrimpPrice = shrimpPriceStore.shrimpPrices.find((s) => s.id === lastFarmShrimpPrice?.shrimp_price_id);
    (shrimpPrice
      ? Promise.resolve([ShrimpPrice.hydrate({...shrimpPrice})])
      : ShrimpPrice.fetch({ id__eq: lastFarmShrimpPrice.shrimp_price_id })
    ).then((shrimpPrices: Array<ShrimpPrice>) => {
      const shrimpPrice = shrimpPrices[0]
      setModelFarmShrimpPrice(shrimpPrice)
    }).catch()
  }, [lastFarmShrimpPrice, shrimpPriceStore, activeBatchStore])

  const onPressSetting = useCallback(() => {
    const params = farmShrimpPrice ? farmShrimpPrice.toArray() : null
    navigate('Home.Stack', 'Home.FarmShrimpPrice.Create', params)
    JalaAnalytic.log('Mobile app: Home - Overview - Revenue potential - Shrimp price - Click');
  }, [farmShrimpPrice])

  const price = React.useMemo(() => {
    const sizePrice = farmShrimpPrice
      ? `${currency}. ${numeral(farmShrimpPrice.size_100).format('0,0')}/kg (${translate('shrimpPriceScreen.size100')})`
      : translate('overviewRevenuePotentialListScreen.notConnectedPrice')
    return sizePrice
  }, [currency, farmShrimpPrice])

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <View flexDirection="row" justifyContent="space-between">
        <View flex={1} paddingRight="8px">
          <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} isTruncated numberOfLines={1} />
        </View>
        <Button
          testID="jala:id/setting-button"
          colorScheme={colorScheme}
          tx="overviewRevenuePotentialListScreen.settingShrimpPrice"
          preset="link"
          textPreset="title4"
          style={SETTING_BUTTON}
          onPress={onPressSetting}
        />
      </View>
      <Text tx="overviewRevenuePotentialListScreen.subtitle" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral(predictionRevenue / 1000000).format('0,0.[00]')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCyclePrediction && color.palette.dust500 || undefined}
        />
        <Text tx="million" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      <View flexDirection={"row"} flexWrap={"wrap"} marginTop={"4px"} alignItems={"center"}>
        <Text text={translate("overviewRevenuePotentialListScreen.sellingPrice")} preset="caption2" colorScheme={colorScheme} />
        <Text marginX={"4px"} text={price} preset="title5" colorScheme={colorScheme} />
        {!farmShrimpPrice && <Entypo name="warning" size={12} color={color.palette.orange500} />}
      </View>
      {!isFeedActualWithin4Days && <InfoWarningModal />}
    </View>
  )
})

const TableHeader: FC<{ colorScheme?: "light"|"dark", currency?: string }> = (props) => {
  const { currency } = props;

  return (
    <View style={HEADER}>
      <View width="23%" paddingX="8px" alignItems="flex-start">
        <Text tx="overviewRevenuePotentialListScreen.pond" preset="body3" color={color.palette.gray800} numberOfLines={2} />
      </View>
      <View width="27%" paddingX="8px" alignItems="flex-end">
        <Text tx="overviewRevenuePotentialListScreen.revenuePotential" preset="body3" color={color.palette.gray800} numberOfLines={2} />
      </View>
      <View width="23%" paddingX="8px" alignItems="flex-end">
        <Text tx="overviewRevenuePotentialListScreen.biomassEst" preset="body3" color={color.palette.gray800} numberOfLines={2}  textAlign="right" />
      </View>
      <View width="27%" paddingX="8px" alignItems="flex-end">
        <Text text={translate("overviewRevenuePotentialListScreen.harvest", { currency: currency || 'Rp' })} preset="body3" color={color.palette.gray800} numberOfLines={2} />
      </View>
    </View>
  )
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props
  const [potentialRevenue, setPotentialRevenue] = useState(0)
  const [totalWeight, setTotalWeight] = useState(0)
  const [harvestRevenue, setHarvestRevenue] = useState(0)

  const { item: { age, pond_name, cycle_actuals, cycle_predictions} } = info;
  useEffect(() => {
    const actualCycle = cycle_actuals.find((c) => c.age === age) || {}
    if (actualCycle.potential_revenue) {
      setPotentialRevenue(actualCycle.potential_revenue)
      setTotalWeight(actualCycle.total_weight)
      setHarvestRevenue(actualCycle.revenue_accumulation)
    } else {
      const predictionCycle = cycle_predictions.find((c) => c.age === age) || {}
      setPotentialRevenue(predictionCycle.potential_revenue)
      setTotalWeight(predictionCycle.total_weight)
      setHarvestRevenue(predictionCycle.revenue_accumulation)
    }
  }, [age, cycle_actuals, cycle_predictions])

  return (
    <View style={CONTENT_ITEM}>
      <View width="23%" paddingX="8px" py="10px" alignItems="flex-start">
        <Text ellipsizeMode="middle" text={`${pond_name} (${age})`} preset="body3" colorScheme={colorScheme} numberOfLines={1} />
      </View>
      <View width="27%" paddingX="8px" py="10px" alignItems="flex-end" backgroundColor={color.palette.gray100}>
        <Text text={numeral(potentialRevenue).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="23%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(totalWeight).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="27%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(harvestRevenue).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
});

export const OverviewRevenuePotentialListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewRevenuePotentialListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore, farm } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})
  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])
  const [showHeader, setShowHeader] = useState<boolean>(false)
  const [currency, setCurrency] = useState<string>()

  useEffect(() => {
    if (farm?.currency_id) {
      Currency.fetch({
        id__eq: farm?.currency_id
      }).then(models => models.map(model => model._attributes)).then(values => {
        setCurrency(values.length ? values[0].symbol : farm?.currency_id)
      })
    }
  }, [farm])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useFocusEffect(useCallback(() => {
    farmStore.getFarmShrimpPrices()
  }, [farmStore]))

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: Home - Overview - Revenue potential - View');
  }, []))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {showHeader && <TableHeader colorScheme={colorScheme} />}
      <FlatList
        data={getSnapshot(cycles)}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={<>
          <Header colorScheme={colorScheme} currency={currency}  />
          <InfoItem colorScheme={colorScheme}>
            <Text preset="title5" color={color.palette.orange700} tx="overviewRevenuePotentialListScreen.infoCostFormula" txOptions={{
              currency
            }} />
            </InfoItem>
          <TableHeader colorScheme={colorScheme} currency={currency} />
        </>}
        ListEmptyComponent={<ListEmpty colorScheme={colorScheme} height={`${height - 270}px`} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={!!loadingCyclePrediction}
        onScroll={({nativeEvent}) => {
          setShowHeader(nativeEvent.contentOffset.y>200)
        }}
      />
      <ItemFooter colorScheme={colorScheme} />
    </Screen>
  )
})
